import { useEffect, useMemo, useRef, useState } from 'react';

import { ctw } from '@/common';
import { Download, Pause, Play } from '../../../../../common/icons/lucide-adapter';
import { isAbsoluteHttpUrl } from './utils';

type CallRecordingPlayerProps = {
  url: string;
  /** Authored duration, shown until the audio metadata reports the real one. */
  durationSeconds?: number | null;
};

const BAR_COUNT = 48;

export const formatCallDuration = (totalSeconds: number | null | undefined): string => {
  if (totalSeconds == null || !Number.isFinite(totalSeconds) || totalSeconds < 0) {
    return '--:--';
  }

  const minutes = Math.floor(totalSeconds / 60);
  const seconds = Math.floor(totalSeconds % 60);

  return `${minutes}:${String(seconds).padStart(2, '0')}`;
};

// Deterministic pseudo-waveform: the bars are a styled seek track (click to
// jump), NOT real amplitude data — we only have an MP3 URL, no analysis. Seeded
// from the URL so a given recording always renders the same shape.
const buildBars = (seedSource: string): number[] => {
  let seed = 0;

  for (let i = 0; i < seedSource.length; i++) {
    seed = (seed * 31 + seedSource.charCodeAt(i)) % 233_280;
  }

  return Array.from({ length: BAR_COUNT }, (_, i) => {
    seed = (seed * 9301 + 49_297) % 233_280;

    const noise = seed / 233_280;
    const envelope = 0.55 + 0.45 * Math.sin((i / BAR_COUNT) * Math.PI);

    return 0.25 + 0.75 * noise * envelope;
  });
};

export const CallRecordingPlayer = ({ url, durationSeconds }: CallRecordingPlayerProps) => {
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [loadedDuration, setLoadedDuration] = useState<number | null>(null);

  const bars = useMemo(() => buildBars(url), [url]);
  const duration = loadedDuration ?? durationSeconds ?? null;
  const progress = duration ? Math.min(currentTime / duration, 1) : 0;

  useEffect(() => {
    const audio = audioRef.current;

    if (!audio) return;

    const onTime = () => setCurrentTime(audio.currentTime);
    const onLoaded = () => {
      if (Number.isFinite(audio.duration)) setLoadedDuration(audio.duration);
    };
    const onEnded = () => setIsPlaying(false);

    audio.addEventListener('timeupdate', onTime);
    audio.addEventListener('loadedmetadata', onLoaded);
    audio.addEventListener('ended', onEnded);

    return () => {
      audio.removeEventListener('timeupdate', onTime);
      audio.removeEventListener('loadedmetadata', onLoaded);
      audio.removeEventListener('ended', onEnded);
    };
  }, []);

  const togglePlayback = () => {
    const audio = audioRef.current;

    if (!audio) return;

    if (isPlaying) {
      audio.pause();
      setIsPlaying(false);
    } else {
      void audio.play().then(
        () => setIsPlaying(true),
        // Autoplay/network failure — the download link remains the fallback.
        () => setIsPlaying(false),
      );
    }
  };

  const seekToRatio = (ratio: number) => {
    const audio = audioRef.current;

    if (!audio || duration == null) return;

    const clamped = Math.min(Math.max(ratio, 0), 1);

    audio.currentTime = clamped * duration;
    setCurrentTime(audio.currentTime);
  };

  return (
    <div className="flex h-full items-center gap-4 rounded-2xl border border-gray-200 px-5 py-4">
      <audio ref={audioRef} src={url} preload="metadata" />

      <button
        type="button"
        onClick={togglePlayback}
        aria-label={isPlaying ? 'Pause recording' : 'Play recording'}
        className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-violet-100 text-violet-700 transition-colors hover:bg-violet-200"
      >
        {isPlaying ? (
          <Pause className="h-5 w-5 fill-current" />
        ) : (
          <Play className="h-5 w-5 fill-current" />
        )}
      </button>

      <div className="min-w-0 flex-1">
        <div className="text-sm font-semibold text-slate-900">Voice Recording</div>
        <div
          role="slider"
          aria-label="Seek within recording"
          aria-valuemin={0}
          aria-valuemax={duration ?? 0}
          aria-valuenow={Math.round(currentTime)}
          tabIndex={0}
          className="mt-2 flex h-8 cursor-pointer items-end gap-[2px]"
          onClick={event => {
            const rect = event.currentTarget.getBoundingClientRect();

            seekToRatio((event.clientX - rect.left) / rect.width);
          }}
          onKeyDown={event => {
            if (duration == null) return;

            if (event.key === 'ArrowRight') seekToRatio((currentTime + 5) / duration);

            if (event.key === 'ArrowLeft') seekToRatio((currentTime - 5) / duration);
          }}
        >
          {bars.map((height, index) => {
            const isPlayed = index / BAR_COUNT <= progress && progress > 0;

            return (
              <span
                key={index}
                className={ctw(
                  'w-full min-w-[2px] flex-1 rounded-sm',
                  isPlayed ? 'bg-violet-600' : 'bg-gray-200',
                )}
                style={{ height: `${Math.round(height * 100)}%` }}
              />
            );
          })}
        </div>
      </div>

      <span className="shrink-0 text-sm tabular-nums text-slate-500">
        {isPlaying || currentTime > 0
          ? `${formatCallDuration(currentTime)} / ${formatCallDuration(duration)}`
          : formatCallDuration(duration)}
      </span>

      {isAbsoluteHttpUrl(url) && (
        <a
          href={url}
          download
          target="_blank"
          rel="noopener noreferrer"
          aria-label="Download recording as MP3"
          className="shrink-0 text-slate-400 transition-colors hover:text-slate-600"
        >
          <Download className="h-4 w-4" />
        </a>
      )}
    </div>
  );
};
